import { AnimatePresence, motion } from "framer-motion";
import { Heart, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { Button } from "@/shared/ui/button";
import { DashboardRenderer } from "./DashboardRenderer";
import { useSavedComponents } from "./SavedComponentsProvider";
import type { SavedComponent } from "./useSavedComponents";
import type { DashboardSpec } from "./types";
import { DURATION, EASE } from "./motion";

/** Wrap a single saved section in a minimal spec so DashboardRenderer can
 *  render it standalone (staticMode = no per-section motion, save buttons off). */
function toSpec(item: SavedComponent): DashboardSpec {
  return {
    title: "",
    question: item.panelQuestion,
    sections: [item.section],
    followups: [],
    generated_at: "",
  };
}

interface MyDashboardSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** "My Dashboard" — the collection of components the user has saved. Each card
 *  re-renders the saved section verbatim, with an explicit remove + undo. */
export function MyDashboardSheet({ open, onOpenChange }: MyDashboardSheetProps) {
  const { saved, remove, restore } = useSavedComponents();

  const handleRemove = (item: SavedComponent) => {
    remove(item.id);
    toast("Removed from My Dashboard", {
      action: { label: "Undo", onClick: () => restore(item) },
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-2xl">
        <SheetHeader className="px-6 pt-6">
          <SheetTitle className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            <Heart className="h-5 w-5 fill-primary text-primary" />
            My Dashboard
          </SheetTitle>
          <SheetDescription>
            {saved.length > 0
              ? `${saved.length} saved ${saved.length === 1 ? "component" : "components"}`
              : "Components you save appear here."}
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 pb-10 pt-4">
          {saved.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <Heart className="h-10 w-10 text-muted-foreground/40" />
              <p className="max-w-xs text-sm text-muted-foreground">
                Like a table or chart with the heart to save it here for quick access.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence initial={false}>
                {saved.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: DURATION.short, ease: EASE.standard }}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-muted-foreground">
                        {item.panelQuestion}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                        onClick={() => handleRemove(item)}
                        aria-label="Remove from My Dashboard"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <DashboardRenderer spec={toSpec(item)} staticMode showSaveButtons={false} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
